import React, { useEffect, useMemo, useState, Component } from "react";

class HubErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  render() {
    if (this.state.err) {
      return (
        <div style={{ padding: 24, fontFamily: "monospace", color: "#ff5d6c", background: "rgba(255,93,108,0.06)", border: "1px solid rgba(255,93,108,0.2)", borderRadius: 12, margin: 12 }}>
          <p style={{ fontWeight: 700, marginBottom: 8 }}>Hub render error — check console</p>
          <p style={{ fontSize: 12, opacity: 0.7 }}>{String(this.state.err?.message || this.state.err)}</p>
        </div>
      );
    }
    return this.props.children;
  }
}
import "./arena.css";
import {
  Radar, Cpu, Settings as SettingsIcon, Volume2, VolumeX, Phone,
  RefreshCw, Bell, BellRing, LineChart, Brain, Users, FlaskConical, Eye, MessagesSquare,
} from "lucide-react";

import ApiTradingPage from "./ApiTradingPage";
import TeamPage from "./TeamPage";
import PositionChartCard from "./PositionChartCard";
import MindPage from "./MindPage";
import OracleDeck from "./OracleDeck";
import MobileNavV2 from "./MobileNavV2";
import TradingArenaPage from "./TradingArenaPage";
import HubCommandPage from "./HubCommandPage";
import ResearchSection from "../research/ResearchSection";
import { researchNavigate } from "../research/desk/researchNavigation";
import MarketRoomPage from "./MarketRoomPage";
import AgentBar from "./AgentBar";
import { isSoundEnabled, setSoundEnabled, sfx } from "./SoundEngine";
import { buildAeonHubState } from "../hub/aeonHubData";

const TABS = [
  { id: "command",    label: "Brief",      icon: Radar },
  { id: "trading",    label: "Markets",    icon: LineChart },
  { id: "room",       label: "Room",       icon: MessagesSquare },
  { id: "engines",    label: "API Trading", icon: Cpu },
  { id: "team",       label: "Desk",       icon: Users },
  { id: "mind",       label: "Review",     icon: Brain },
  { id: "oracle",     label: "Outlook",    icon: Eye },
  { id: "researcher", label: "Research",   icon: FlaskConical },
];

const TAB_DESCRIPTIONS = {
  command: "What matters right now",
  trading: "Prices, positions and execution",
  room: "Shared market intelligence",
  engines: "WEEX routing and AEON calls",
  team: "People and agent operations",
  mind: "Performance and decisions",
  oracle: "Forward-looking signals",
  researcher: "Daily research, market context and strategy lab",
};

function ambientModeFromState(systemHealth, regimeBanner) {
  if (regimeBanner) return "active";
  const overall = String(systemHealth?.overall || "").toLowerCase();
  if (overall.includes("crisis") || overall.includes("critical")) return "crisis";
  if (overall.includes("degraded") || overall.includes("warn")) return "active";
  return "calm";
}

export default function ArenaShell(props) {
  const {
    stats, tradingStats, marketData, messages, operatorSummary, portfolioSummary,
    governanceSummary, systemHealth, fwSummary, openPositions, btcPrice,
    wsConnected, unreadAlerts, regimeBanner, notificationsEnabled, refreshing,
    actions = {}, onLeaveArena,
  } = props;

  // A #research/... hash selects the Research tab, so the routes in the new
  // Research workspace (#research/strategies/:key) are deep-linkable on a cold
  // load. Without this the hash was only honoured once you were already there.
  const tabFromHash = () => (typeof window !== "undefined"
    && ((window.location.hash || "").startsWith("#research/")
      || /^\/research(?:\/|$)/.test(window.location.pathname)) ? "researcher" : "command");

  const [activeTab, setActiveTab] = useState(tabFromHash);

  useEffect(() => {
    const onHash = () => {
      if ((window.location.hash || "").startsWith("#research/")) setActiveTab("researcher");
    };
    window.addEventListener("hashchange", onHash);
    const onPath = () => setActiveTab(tabFromHash());
    window.addEventListener("popstate", onPath);
    return () => {
      window.removeEventListener("hashchange", onHash);
      window.removeEventListener("popstate", onPath);
    };
  }, []);
  function selectTab(id) {
    if (id === "researcher") researchNavigate("/research");
    else if (/^\/research(?:\/|$)/.test(window.location.pathname) || window.location.hash.startsWith("#research/")) researchNavigate("/");
    setActiveTab(id);
  }
  const [soundOn, setSoundOnState] = useState(isSoundEnabled());

  const hubState = useMemo(() => buildAeonHubState({
    stats, tradingStats, marketData, messages, operatorSummary, portfolioSummary,
    governanceSummary, systemHealth, fwSummary, openPositions, btcPrice, wsConnected,
  }), [stats, tradingStats, marketData, messages, operatorSummary, portfolioSummary, governanceSummary, systemHealth, fwSummary, openPositions, btcPrice, wsConnected]);

  const totalPnl = Number(tradingStats?.total_pnl_usd ?? tradingStats?.total_pnl ?? portfolioSummary?.aggregate_pnl ?? 0);
  const winRate = Number(tradingStats?.win_rate || 0);
  const totalTrades = Number(tradingStats?.total_trades || 0);

  const ambientMode = ambientModeFromState(systemHealth, regimeBanner);
  const activeSection = TABS.find((tab) => tab.id === activeTab) || TABS[0];

  function toggleSound() {
    const next = !soundOn;
    setSoundEnabled(next);
    setSoundOnState(next);
    if (next) sfx.tap();
  }

  return (
    <div className="arena-root" data-mode={ambientMode}>
      {ambientMode === "crisis" ? <div className="arena-crisis-vignette" aria-hidden /> : null}

      <main className={`arena-shell-inner ${activeTab === "mind" ? "is-review-workspace" : ""}`}>
        <header className="arena-app-header">
          <div className="arena-brand-lockup">
            <span className="arena-brand-mark" aria-hidden>
              <span>A</span>
              <i />
            </span>
            <span className="arena-brand-copy">
              <strong>AEON</strong>
              <small>Market intelligence</small>
            </span>
            <span className="arena-edition-badge">Local / 01</span>
          </div>

          <nav className="arena-desktop-nav" aria-label="Primary navigation">
            {TABS.map((tab, index) => {
              const Icon = tab.icon;
              const isActive = tab.id === activeTab;
              return (
                <button
                  key={tab.id}
                  type="button"
                  className={`arena-desktop-nav-item ${isActive ? "is-active" : ""}`}
                  onClick={() => selectTab(tab.id)}
                  aria-current={isActive ? "page" : undefined}
                  title={TAB_DESCRIPTIONS[tab.id]}
                >
                  <span className="arena-nav-index">{String(index + 1).padStart(2, "0")}</span>
                  <Icon />
                  <span className="arena-nav-label">{tab.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="arena-rail-context">
            <span>Current view</span>
            <strong>{activeSection.label}</strong>
            <small>{TAB_DESCRIPTIONS[activeTab]}</small>
          </div>

          <div className="arena-header-actions">
            <span className={`arena-connection ${wsConnected ? "is-live" : "is-offline"}`}>
              <span />
              {wsConnected ? "Live" : "Offline"}
            </span>
            <IconBtn onClick={toggleSound} active={soundOn} title={soundOn ? "Sound on" : "Sound off"}>
              {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </IconBtn>
            <IconBtn onClick={actions.toggleNotifications} active={notificationsEnabled} title={notificationsEnabled ? "Notifications on" : "Enable notifications"}>
              {notificationsEnabled ? <BellRing className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
            </IconBtn>
            <IconBtn onClick={actions.refresh} spinning={refreshing} title="Refresh">
              <RefreshCw className="h-4 w-4" />
            </IconBtn>
            {actions.openVoice ? (
              <IconBtn onClick={actions.openVoice} title="Talk to AEON" highlight>
                <Phone className="h-4 w-4" />
              </IconBtn>
            ) : null}
            <button type="button" className="arena-classic-button" onClick={onLeaveArena} title="Open classic interface">
              <SettingsIcon />
              <span>System</span>
            </button>
          </div>

          <div className="arena-rail-foot">
            <span>Private workspace</span>
            <small>Local session</small>
          </div>
        </header>

        <div className="arena-mobile-header">
          <div className="arena-brand-lockup">
            <span className="arena-brand-mark" aria-hidden><Radar /></span>
            <span className="arena-brand-copy">
              <strong>AEON</strong>
              <small>{activeSection.label} / {TAB_DESCRIPTIONS[activeTab]}</small>
            </span>
          </div>
          <div className="arena-header-actions">
            <span className={`arena-connection ${wsConnected ? "is-live" : "is-offline"}`} aria-label={wsConnected ? "Live connection" : "Offline"}>
              <span />
            </span>
            <IconBtn onClick={actions.refresh} spinning={refreshing} title="Refresh">
              <RefreshCw className="h-4 w-4" />
            </IconBtn>
          </div>
        </div>

        {activeTab !== "mind" ? (
          <div className="arena-command-dock">
            <HubErrorBoundary>
              <AgentBar section={activeTab} />
            </HubErrorBoundary>
          </div>
        ) : null}

        <div className="arena-view">
          {activeTab === "command" ? (
            <HubErrorBoundary>
              <HubCommandPage
                hubState={hubState}
                messages={messages}
                btcPrice={btcPrice}
                wsConnected={wsConnected}
                totalPnl={totalPnl}
                winRate={winRate}
                totalTrades={totalTrades}
                dataAvailability={{
                  performance: Boolean(tradingStats && Object.keys(tradingStats).length),
                  portfolio: Boolean(
                    (operatorSummary && Object.keys(operatorSummary).length)
                    || (portfolioSummary && Object.keys(portfolioSummary).length)
                  ),
                  system: Boolean(systemHealth?.services && Object.keys(systemHealth.services).length),
                  engines: Boolean(
                    governanceSummary?.states?.length
                    || (systemHealth?.services && Object.keys(systemHealth.services).length)
                  ),
                }}
                onTabChange={selectTab}
                onTalk={actions.openVoice}
              />
            </HubErrorBoundary>
          ) : null}

          {activeTab === "trading" ? (
            <div className="trading-workspace-mount">
              <TradingArenaPage
                portfolioSummary={portfolioSummary}
                openPositions={openPositions}
                refreshTick={refreshing}
                marketChart={(positions) => (
                  <HubErrorBoundary>
                    <PositionChartCard positions={positions} />
                  </HubErrorBoundary>
                )}
              />
            </div>
          ) : null}

          {activeTab === "room" ? (
            <HubErrorBoundary>
              <MarketRoomPage marketData={marketData} wsConnected={wsConnected} />
            </HubErrorBoundary>
          ) : null}

          {activeTab === "engines" ? (
            <HubErrorBoundary>
              <ApiTradingPage messages={messages} />
            </HubErrorBoundary>
          ) : null}

          {activeTab === "team" ? (
            <HubErrorBoundary>
              <TeamPage />
            </HubErrorBoundary>
          ) : null}

          {activeTab === "mind" ? (
            <HubErrorBoundary>
              <MindPage />
            </HubErrorBoundary>
          ) : null}

          {activeTab === "oracle" ? (
            <HubErrorBoundary>
              <OracleDeck />
            </HubErrorBoundary>
          ) : null}

          {activeTab === "researcher" ? (
            <HubErrorBoundary>
              <ResearchSection />
            </HubErrorBoundary>
          ) : null}
        </div>
      </main>

      <MobileNavV2
        tabs={TABS}
        activeId={activeTab}
        onChange={selectTab}
        onFab={actions.openVoice}
        fabIcon={Phone}
      />

    </div>
  );
}

function IconBtn({ children, onClick, active, spinning, highlight, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`arena-icon-button ${highlight ? "is-highlighted" : ""} ${active ? "is-active" : ""}`}
    >
      <span className={spinning ? "arena-icon-spin" : ""}>{children}</span>
    </button>
  );
}
